import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown, faMessageBot } from "@fortawesome/pro-regular-svg-icons";

import { HelpChatRole, TicketTranscriptEntry } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

interface TicketTranscriptProps {
	entries: TicketTranscriptEntry[];
	/** The customer's first name, shown on the USER turns. */
	customerName: string;
}

/** The help chat that preceded a chat-bot ticket — collapsed by default (PREC-3938 decision 4). */
export const TicketTranscript: FunctionComponent<TicketTranscriptProps> = ({
	entries,
	customerName,
}) => {
	const { t } = useTranslation();

	return (
		<details className="group rounded-xl border border-dashed border-gray-200">
			<summary className="flex cursor-pointer list-none items-center gap-2 px-3.5 py-2.5 text-xs font-semibold text-text-meta">
				<Icon icon={faMessageBot} />
				{t("support.detail.thread.transcript")}
				<span className="font-normal">
					{t("support.detail.thread.transcriptCount", { count: entries.length })}
				</span>
				<Icon
					className="ml-auto transition-transform duration-200 group-open:rotate-180"
					icon={faChevronDown}
				/>
			</summary>
			<div className="flex flex-col gap-2 border-t border-dashed border-gray-200 px-3.5 py-3 text-xs">
				{entries.map((entry, index) => (
					// Transcript turns carry no id; their position is their identity.
					// eslint-disable-next-line @eslint-react/no-array-index-key
					<div key={index} className="flex gap-2">
						<span
							className={classNames(
								"w-20 shrink-0 font-semibold",
								entry.role === HelpChatRole.USER ? "text-brand-light" : "text-text-meta"
							)}
						>
							{entry.role === HelpChatRole.USER
								? customerName
								: t("support.detail.thread.assistant")}
						</span>
						<span className="whitespace-pre-wrap text-text">{entry.content}</span>
					</div>
				))}
			</div>
		</details>
	);
};
